import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { get, post, put } from './api';
import { t, LanguageSwitcher } from '@omnifuse/shared/frontend/i18n';
import { SettingsPanel } from '@omnifuse/shared/frontend/SettingsPanel';
import { get as apiGet, post as apiPost, put as apiPut } from './api';

const S = {
  page: { maxWidth: 1100, margin: '0 auto', padding: 24 } as React.CSSProperties,
  card: { background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,.08)', marginBottom: 16 } as React.CSSProperties,
  btn: { padding: '6px 14px', borderRadius: 8, border: '1px solid #ccc', background: '#fff', cursor: 'pointer', fontSize: 13 } as React.CSSProperties,
  btnPrimary: { padding: '6px 14px', borderRadius: 8, border: 'none', background: '#1a73e8', color: '#fff', cursor: 'pointer', fontSize: 13 } as React.CSSProperties,
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 13 },
  th: { textAlign: 'left' as const, padding: '8px 10px', borderBottom: '2px solid #eee', color: '#555' },
  td: { padding: '8px 10px', borderBottom: '1px solid #f0f0f0' },
  tab: (a: boolean) => ({ padding: '8px 18px', cursor: 'pointer', borderBottom: a ? '2px solid #1a73e8' : '2px solid transparent', color: a ? '#1a73e8' : '#555', fontWeight: a ? 600 : 400, fontSize: 14 }) as React.CSSProperties,
  input: { padding: '6px 10px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13, width: '100%' } as React.CSSProperties,
  chip: { display: 'inline-block', margin: '0 6px 6px 0', padding: '2px 10px', borderRadius: 12, fontSize: 12, background: '#e8f0fe' } as React.CSSProperties,
};

function ProfilesView() {
  const [rows, setRows] = useState<any[]>([]);
  const [memory, setMemory] = useState<any>(null);
  const [busy, setBusy] = useState(false);

  const load = () => get('/api/v1/profiles').then((r: any) => setRows(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const build = async () => {
    setBusy(true);
    try { const r: any = await post('/api/v1/profiles/build'); alert(`分析 ${r.data.orders_analyzed} 订单，构建 ${r.data.profiles_built} 个画像`); load(); }
    catch (e: any) { alert(e.message); }
    setBusy(false);
  };

  const open = async (cid: string) => {
    try { const r: any = await get(`/api/v1/memory/${encodeURIComponent(cid)}?app=dashboard`); setMemory(r.data); }
    catch (e: any) { alert(e.message); }
  };

  const addFact = async () => {
    if (!memory) return;
    const key = prompt('事实键（如 skin_type / size_top / allergy）：');
    if (!key) return;
    const value = prompt('值：');
    if (!value) return;
    try { await put(`/api/v1/memory/${encodeURIComponent(memory.customer_id)}/facts`, { key, value }); open(memory.customer_id); }
    catch (e: any) { alert(e.message); }
  };

  return (
    <>
      <div style={{ ...S.card, display: 'flex', gap: 10 }}>
        <button style={S.btnPrimary} disabled={busy} onClick={build}>{busy ? '构建中...' : t('🔄 从订单构建画像')}</button>
      </div>
      {memory && (
        <div style={{ ...S.card, background: '#f8f9fa' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
            <strong>🧠 {memory.customer_email || memory.customer_id} 的记忆</strong>
            <span style={{ display: 'inline-flex', gap: 6 }}>
              <button style={S.btn} onClick={addFact}>{t('＋ 手动添加事实')}</button>
              <button style={S.btn} onClick={() => setMemory(null)}>{t('关闭')}</button>
            </span>
          </div>
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>{t('订单衍生')}</div>
            {Object.entries(memory.derived || {}).map(([k, v]: any) => (
              <span key={k} style={S.chip}>{k}: {Array.isArray(v) ? v.join('/') : String(v)}</span>
            ))}
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>{t('记忆事实')}</div>
            {Object.entries(memory.facts || {}).map(([k, f]: any) => (
              <span key={k} style={{ ...S.chip, background: '#e6f4ea' }}>
                {k}: {f.value} <span style={{ color: '#888' }}>({Math.round(f.confidence * 100)}% · {f.source})</span>
              </span>
            ))}
            {!Object.keys(memory.facts || {}).length && <span style={{ color: '#888', fontSize: 12 }}>{t('暂无事实')}</span>}
          </div>
        </div>
      )}
      <div style={S.card}>
        <table style={S.table}>
          <thead><tr><th style={S.th}>{t('客户')}</th><th style={S.th}>{t('订单数')}</th><th style={S.th}>{t('记忆事实数')}</th><th style={S.th}>{t('更新时间')}</th></tr></thead>
          <tbody>
            {rows.map((r: any) => {
              const d = JSON.parse(r.derived_json || '{}');
              return (
                <tr key={r.customer_id} style={{ cursor: 'pointer' }} onClick={() => open(r.customer_id)}>
                  <td style={{ ...S.td, color: '#1a73e8' }}>{r.customer_email || r.customer_id}</td>
                  <td style={S.td}>{d.total_orders || 0}</td>
                  <td style={S.td}>{r.fact_count}</td>
                  <td style={S.td}>{(r.updated_at || '').slice(0, 16)}</td>
                </tr>
              );
            })}
            {rows.length === 0 && <tr><td style={S.td} colSpan={4}>{t('暂无画像 — 点击"从订单构建画像"')}</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

function IngestView() {
  const [cid, setCid] = useState('');
  const [text, setText] = useState('');
  const [result, setResult] = useState<any>(null);
  const [busy, setBusy] = useState(false);

  const ingest = async () => {
    if (!cid || !text) return alert('客户 ID 与对话内容必填');
    setBusy(true);
    try { const r: any = await post('/api/v1/memory/ingest', { customer_id: cid, conversation_text: text }); setResult(r.data); }
    catch (e: any) { alert(e.message); }
    setBusy(false);
  };

  return (
    <div style={S.card}>
      <h3 style={{ fontSize: 15, marginBottom: 12 }}>{t('从对话提取偏好（粘贴客服/导购对话记录）')}</h3>
      <input style={{ ...S.input, marginBottom: 8 }} placeholder={t('客户 ID（如 gid://shopify/Customer/123）')} value={cid} onChange={e => setCid(e.target.value)} />
      <textarea style={{ ...S.input, height: 180, marginBottom: 8 }} placeholder={t('对话记录...')} value={text} onChange={e => setText(e.target.value)} />
      <button style={S.btnPrimary} disabled={busy} onClick={ingest}>{busy ? '提取中...' : t('🤖 提取偏好事实')}</button>
      {result && (
        <div style={{ marginTop: 12, fontSize: 13 }}>
          提取到 <strong>{result.facts_extracted}</strong> 条事实：
          {(result.facts || []).map((f: any, i: number) => (
            <span key={i} style={S.chip}>{f.key}: {f.value}</span>
          ))}
        </div>
      )}
    </div>
  );
}

function App() {
  const [tab, setTab] = useState('profiles');
  return (
    <div style={S.page}>
      <div style={{ position: 'fixed', top: 12, right: 16, zIndex: 1000 }}><LanguageSwitcher /></div>
      <h1 style={{ fontSize: 22, marginBottom: 4 }}>CustomerMemoryAI</h1>
      <p style={{ color: '#666', marginBottom: 16 }}>{t('客户偏好记忆 — 深度画像 · 对话提取 · 记忆 API（供导购/Quiz 调用）')}</p>
      <div style={{ display: 'flex', borderBottom: '1px solid #eee', marginBottom: 20 }}>
        <div style={S.tab(tab === 'profiles')} onClick={() => setTab('profiles')}>{t('客户画像')}</div>
        <div style={S.tab(tab === 'ingest')} onClick={() => setTab('ingest')}>{t('对话提取')}</div>
        <div style={S.tab(tab === 'settings')} onClick={() => setTab('settings')}>{t('设置')}</div>
      </div>
      {tab === 'profiles' && <ProfilesView />}
      {tab === 'ingest' && <IngestView />}
      {tab === 'settings' && <SettingsPanel api={{ get: apiGet, post: apiPost, put: apiPut }} />}
    </div>
  );
}

const root = createRoot(document.getElementById('root')!);
root.render(<App />);
